import type { CaptureOptions } from "../core/types.js";
import type {
  TelemetryLevel,
  TelemetryMessageLevel,
} from "../core/types.js";
import type { TelemetryRuntimeAPI } from "./api.js";

let boundServer: TelemetryRuntimeAPI | undefined;

const UNBOUND_MESSAGE =
  "[telemetry] Server runtime is not bound. Call bindServer(init.api) in entry.server after await telemetry.initServer().";

export function bindServer(api: TelemetryRuntimeAPI): void {
  boundServer = api;
}

export function unbindServer(): void {
  boundServer = undefined;
}

export function getServer(): TelemetryRuntimeAPI {
  if (!boundServer) {
    throw new Error(UNBOUND_MESSAGE);
  }
  return boundServer;
}

export function log(
  level: TelemetryLevel,
  message: string,
  attributes?: Record<string, unknown>,
): void {
  getServer().log(level, message, attributes);
}

export function captureError(error: unknown, options?: CaptureOptions): void {
  getServer().captureError(error, options);
}

export function captureUnhandledRejection(
  reason: unknown,
  options?: CaptureOptions,
): void {
  getServer().captureUnhandledRejection(reason, options);
}

export function captureRouteError(
  error: unknown,
  routeContext: { routeId?: string; route?: string },
  options?: CaptureOptions,
): void {
  getServer().captureRouteError(error, routeContext, options);
}

export function captureMessage(
  message: string,
  level?: TelemetryMessageLevel,
  extra?: Record<string, unknown>,
): void {
  getServer().captureMessage(message, level, extra);
}

export function captureCustom(
  name: string,
  message: string,
  metadata?: Record<string, unknown>,
): void {
  getServer().captureCustom(name, message, metadata);
}

export function addBreadcrumb(
  message: string,
  options?: {
    category?: string;
    level?: TelemetryLevel;
    data?: Record<string, unknown>;
  },
): void {
  getServer().addBreadcrumb(message, options);
}

export async function flush(): Promise<void> {
  await getServer().flush();
}
